import React, {useCallback, useEffect, useState} from "react";
import "../styles/Appeal.css";
import { useLocation } from "react-router-dom";
import Papa from "papaparse";
import { saveAs } from "file-saver";

interface Appeal {
  [key: string]: any;
}

type FieldMapping = {
  [key: string]: string[];
};

type ColumnVisibility = {
  [key: string]: boolean;
};

type StatusFilter = "default" | "success" | "inQueue";

const Appeals: React.FC = () => {
  const [appeals, setAppeals] = useState<Appeal[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("default");
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>({
    "Номер обращения": true,
    Дата: true,
    "Описание проблемы": true,
    "Критичность проблемы": true,
    Адрес: true,
    "Прикрепленные файлы": true,
    "Серийный номер": true,
    // "Номер продукта": false,
    "Имя продукта": true,
    "№ договора тех поддержки": true,
    "Дата начала ТП": true,
    "Дата окончания ТП": true,
    "Уровень поддержки": true,
    Статус: true,
    "Дата закрытия": true,
    Сотрудник: true,
    "E-mail сотрудника": true,
  });
  const location = useLocation();

  const fetchAppeals = useCallback(() => {
    const requestData = {
      action: "GET_APPEALS",
      client_id: location.state.userId,
    };
    const jsonString = JSON.stringify(requestData);

    if (window.ws && window.ws.readyState === WebSocket.OPEN) {
      window.ws.send(jsonString);
    } else {
      console.error("WebSocket не доступен");
    }
  },[location.state.userId]);

  const handleMessage = useCallback( (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      if (data.appeals) {
        const sortedAppeals = data.appeals.sort((a: Appeal, b: Appeal) => {
          const numberA = parseInt(getFieldValue(a, "Номер обращения"), 10);
          const numberB = parseInt(getFieldValue(b, "Номер обращения"), 10);
          return numberB - numberA; // Сортировка по убыванию
        });
        setAppeals(sortedAppeals);
      }
    } catch (error) {
      console.error("Ошибка при разборе JSON:", error);
    }
  },[]);

  useEffect(() => {
    fetchAppeals();

    window.ws.addEventListener("message", handleMessage);

    return () => {
      window.ws.removeEventListener("message", handleMessage);
    };
  }, [fetchAppeals, handleMessage]);

  const getFieldValue = (appeal: Appeal, fieldName: string): any => {
    const possibleFields: FieldMapping = {
      "Номер обращения": ["appeal_number", "appealNumber", "number"],
      Дата: ["Date", "date", "created_at", "createdAt"],
      "Описание проблемы": [
        "Problem description",
        "Problem_description",
        "problemDescription",
        "description",
        "problem",
      ],
      Адрес: ["put_addres", "putAddress", "address"],
      "Прикрепленные файлы": ["attached_files", "attachedFiles", "files"],
      "Серийный номер": ["serial_number", "serialNumber"],
      "Номер продукта": ["product_number", "productNumber"],
      "Имя продукта": ["product", "productName"],
      "№ договора тех поддержки": [
        "supp_number",
        "suppNumber",
        "supportNumber",
      ],
      "Дата начала ТП": ["support_start_date", "supportStartDate", "startDate"],
      "Дата окончания ТП": ["support_end_date", "supportEndDate", "endDate"],
      "Уровень поддержки": ["critical_rate", "criticalRate", "criticality"],
      "Критичность проблемы": ["problem_lvl", "problemLevel", "problemLvl"],
      Статус: ["status", "appealStatus"],
      "Дата закрытия": ["close_date", "closeDate", "closedAt"],
      Сотрудник: ["employee", "employeeName", "employee_name"],
      "E-mail сотрудника": ["employee_email", "employeeEmail", "email"],
    };

    const fields = possibleFields[fieldName] || [];
    const field = fields.find((f) => f in appeal);

    if (field) {
      const value = appeal[field];
      return Array.isArray(value) ? value : value ? [value] : [];
    }

    return []; // Возвращаем пустой массив, если поле не найдено
  };

  const toggleColumnVisibility = (columnName: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  const onFilterStatus = () => {
    if (statusFilter === "default") {
      setAppeals((prev) =>
        prev.sort((a, b) => (b.status === "Успех" ? 1 : -1)),
      );
      setStatusFilter("success");
    } else if (statusFilter === "success") {
      setAppeals((prev) =>
        prev.sort((a, b) => (b.status === "В очереди" ? 1 : -1)),
      );
      setStatusFilter("default");
    }
  };
  const onDownloadCsv = () => {
    const data = appeals.map((el) => ({
      "Номер заявки": el.appeal_number,
      "Серийный номер": el.serial_number,
      // "Номер продукта": el.product_number,
      "Продукт": el.product,
      "Договор": el.supp_number,
      "Дата начала поддержки": el.support_start_date,
      "Дата окончания поддержки": el.support_end_date,
      "Адрес установки": el.put_addres,
      "Описание проблемы": el["Problem description"],
      "Критичность проблемы": el.problem_lvl,
      "Уровень поддержки": el.critical_rate,
      "Прикреплённые файлы": el.attached_files,
      "Дата": el.Date,
      "Статус": el.status,
      "Ответственный": el.employee,
      "Email сотрудника": el.employee_email,
      "Дата закрытия": el.close_date,
    }));
    
    const csv = Papa.unparse(data, {
      delimiter: ";", // Разделитель (можно ',' для CSV и ';' для Excel)
      header: true, // Добавляем заголовки
    });

    // Кодировка UTF-8 с BOM для корректного отображения в Excel
    const csvWithBom = "\uFEFF" + csv;

    const blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "data.csv");
  };

  const onUpdateDate = ()=>{
    fetchAppeals()
  }

  if (appeals.length === 0) {
    return <div>Нет обращений</div>;
  }

  return (
    <div className="appeals-container">
      <div className="appeals-containe-row">
        <h2>Список обращений</h2>
        <div className="appeals-row">
          <button onClick={onUpdateDate}>Обновить</button>
          <button onClick={onDownloadCsv}>Скачать обращения</button>
        </div>
      </div>
      <div className="column-toggle">
        <span className="toggle-label">Показать колонки:</span>
        {Object.keys(columnVisibility).map((columnName) => (
          <label key={columnName} className="toggle-item">
            <input
              type="checkbox"
              checked={columnVisibility[columnName]}
              onChange={() => toggleColumnVisibility(columnName)}
            />
            <span className="toggle-text">{columnName}</span>
          </label>
        ))}
      </div>
      <div className="table-responsive">
        <table className="appeals-table">
          <thead>
            <tr>
              {Object.entries(columnVisibility).map(
                ([columnName, isVisible]) =>
                  isVisible && (
                    <th key={columnName}>
                      {columnName === "Статус" ? (
                        <>
                          {columnName}{" "}
                          <button onClick={onFilterStatus}>Фильтр</button>
                        </>
                      ) : (
                        columnName
                      )}
                    </th>
                  ),
              )}
            </tr>
          </thead>
          <tbody>
            {appeals.map((appeal, index) => (
              <tr key={index}>
                {Object.entries(columnVisibility).map(
                  ([columnName, isVisible]) =>
                    isVisible && (
                      <td key={columnName}>
                        {columnName === "Прикрепленные файлы" ? (
                          <div>
                            {getFieldValue(appeal, columnName).length !== 0
                              ? String(getFieldValue(appeal, columnName))
                                  .split(",")
                                  .map((file: string, fileIndex: number) => (
                                    <div key={`${columnName}-${fileIndex}`}>
                                      <a
                                        key={fileIndex}
                                        href={file}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        📎 Файл {fileIndex + 1}
                                      </a>{" "}
                                      <br />
                                      <br />
                                    </div>
                                  ))
                              : "Нет прикрепленных файлов"}
                          </div>
                        ) : (
                          getFieldValue(appeal, columnName)
                        )}
                      </td>
                    ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Appeals;
